import React, {useEffect, useRef} from "react";
import {routePath} from "../auth/roles";
import {OwnSecondFactor} from "../components";
import {NotificationSettings} from "../components/NotificationSettings";
import type {NotificationScope} from "../components/notificationScope";
import {useRoute} from "../hooks";

const SETTLE_MS = 2000;
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"] as const;

interface CabinetSettingsProps {
  scope: NotificationScope;
  className: string;
}

/** «Настройки» кабинета: два блока на одной странице — вход в кабинет и уведомления. */
export function CabinetSettings({scope, className}: CabinetSettingsProps): JSX.Element {
  const path = routePath(useRoute());
  const pageRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLElement>(null);

  // «Настроить» из колокольчика ведёт сразу к блоку уведомлений. Блоки догружаются
  // и растут, поэтому прокрутка повторяется, пока страница меняет размер, —
  // до первого действия пользователя или пока страница не успокоится.
  useEffect(() => {
    const target = notificationsRef.current;
    if (path !== scope.settingsRoute || !target) return undefined;
    const scroll = (): void => target.scrollIntoView?.({block: "start"});
    scroll();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(scroll);
    observer.observe(pageRef.current ?? target);
    const stop = (): void => {
      observer.disconnect();
      window.clearTimeout(timer);
      USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop));
    };
    const timer = window.setTimeout(stop, SETTLE_MS);
    USER_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, {passive: true}));
    return stop;
  }, [path, scope.settingsRoute]);

  return (
    <div ref={pageRef} className={`page ${className}`}>
      <header className="page-header merchant-page-header">
        <div>
          <h1>Настройки</h1>
        </div>
      </header>
      <section className="merchant-settings-group" aria-labelledby="merchant-settings-login" data-testid="settings-login">
        <h2 id="merchant-settings-login" className="merchant-settings-group-title">Вход в кабинет</h2>
        <OwnSecondFactor/>
      </section>
      <section
        ref={notificationsRef}
        className="merchant-settings-group"
        aria-labelledby="merchant-settings-notifications"
        data-testid="settings-notifications"
      >
        <h2 id="merchant-settings-notifications" className="merchant-settings-group-title">Уведомления</h2>
        <NotificationSettings scope={scope}/>
      </section>
    </div>
  );
}
